import { useCallback, useEffect, useRef, useState } from "react";

import { api, unwrap } from "../api/client";
import type { Item } from "../api/runs";

export type ItemFilters = { state?: string; reason?: string; q?: string };

export const PAGE_SIZE = 100;
// A refresh of the loaded rows is one request; past this many, it is a heavy one.
const MAX_REFRESH = 500;

type Query = Record<string, string | number>;

/**
 * One page of a run's numbers at a time.
 *
 * A run can hold a hundred thousand numbers, so the browser never asks for all of them:
 * it takes `PAGE_SIZE` rows in line order, `loadMore()` fetches the next page, and the
 * filters go to the server rather than running here. While the run is live, the poll
 * refreshes only the rows already loaded (`until_line`), so its cost does not grow with
 * the run.
 */
export function useRunItems(runId: string, pollMs: number | false, filters: ItemFilters = {}) {
  const [items, setItems] = useState<Item[]>([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isPending, setIsPending] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  const cursor = useRef<string | null>(null);
  const lastLine = useRef<number | null>(null);
  const loaded = useRef(0);
  const busy = useRef(false);
  // A filter change must not be overtaken by a page still in flight from the old one.
  const generation = useRef(0);

  const { state, reason, q } = filters;
  const fetchPage = useCallback(
    async (query: Query) => {
      const page = await unwrap(
        api.GET("/api/runs/{run_id}/items", {
          params: {
            path: { run_id: runId },
            query: {
              limit: PAGE_SIZE,
              ...(state && state !== "all" ? { state } : {}),
              ...(reason ? { reason } : {}),
              ...(q ? { q } : {}),
              ...query,
            },
          },
        }),
      );
      return page;
    },
    [runId, state, reason, q],
  );

  // The first page: on mount, and again whenever the run or a filter changes.
  useEffect(() => {
    const mine = ++generation.current;
    setIsPending(true);
    setItems([]);
    cursor.current = null;
    lastLine.current = null;
    loaded.current = 0;
    void (async () => {
      try {
        const page = await fetchPage({});
        if (generation.current !== mine) return;
        setItems(page.items);
        setTotal(page.total);
        setHasMore(page.has_more);
        cursor.current = page.cursor;
        lastLine.current = page.items.at(-1)?.line_no ?? null;
        loaded.current = page.items.length;
        setError(null);
        setLastUpdated(Date.now());
      } catch (e) {
        if (generation.current === mine) setError(e);
      } finally {
        if (generation.current === mine) setIsPending(false);
      }
    })();
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (busy.current || !hasMore || lastLine.current === null) return;
    busy.current = true;
    const mine = generation.current;
    setIsLoadingMore(true);
    void (async () => {
      try {
        const page = await fetchPage({ after_line: lastLine.current as number });
        if (generation.current !== mine) return;
        setItems((prev) => {
          const seen = new Set(prev.map((i) => i.id));
          return [...prev, ...page.items.filter((i) => !seen.has(i.id))];
        });
        setTotal(page.total);
        setHasMore(page.has_more);
        lastLine.current = page.items.at(-1)?.line_no ?? lastLine.current;
        loaded.current += page.items.length;
        setError(null);
      } catch (e) {
        if (generation.current === mine) setError(e);
      } finally {
        busy.current = false;
        if (generation.current === mine) setIsLoadingMore(false);
      }
    })();
  }, [fetchPage, hasMore]);

  // While the run is live: refresh what is on screen, never the whole run.
  useEffect(() => {
    if (pollMs === false) return;
    const tick = async () => {
      if (busy.current) return;
      const mine = generation.current;
      const filtered = Boolean((state && state !== "all") || reason || q);
      // A run buying its numbers grows while it is watched. Until the first page is
      // full, the refresh reads it again from the top, so new rows appear by
      // themselves; past that, new rows wait at the end for "load more".
      const reread = filtered || lastLine.current === null || loaded.current < PAGE_SIZE;
      try {
        // Unfiltered, the changed rows are enough. Filtered, a row that no longer
        // matches would never come back and would sit there stale, so the loaded rows
        // are read again instead (bounded by what is on screen).
        const page = await fetchPage(
          reread
            ? { limit: Math.min(Math.max(loaded.current, PAGE_SIZE), MAX_REFRESH) }
            : {
                changed_since: cursor.current ?? "",
                until_line: lastLine.current ?? 0,
                limit: MAX_REFRESH,
              },
        );
        if (generation.current !== mine) return;
        cursor.current = page.cursor;
        setTotal(page.total);
        if (reread) {
          setItems(page.items);
          loaded.current = page.items.length;
          lastLine.current = page.items.at(-1)?.line_no ?? null;
          setHasMore(page.has_more);
        } else {
          if (page.items.length) {
            setItems((prev) => {
              const fresh = new Map(page.items.map((i) => [i.id, i]));
              let changed = false;
              const next = prev.map((row) => {
                const update = fresh.get(row.id);
                if (!update || update.updated_at === row.updated_at) return row;
                changed = true;
                return update;
              });
              return changed ? next : prev;
            });
          }
          // Rows added past the loaded ones: offer them at the end of the list.
          if (page.total > loaded.current) setHasMore(true);
        }
        setError(null);
        setLastUpdated(Date.now());
      } catch (e) {
        if (generation.current === mine) setError(e);
      }
    };
    const id = window.setInterval(() => void tick(), pollMs);
    return () => window.clearInterval(id);
  }, [pollMs, fetchPage, state, reason, q]);

  return { items, total, hasMore, loadMore, isPending, isLoadingMore, error, lastUpdated };
}
